import React, { useState, useEffect } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Plus, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';

// Mock partners data - replace with actual API call
const mockPartners = [
  { id: 1, name: 'Partner A', email: 'partnera@example.com' },
  { id: 2, name: 'Partner B', email: 'partnerb@example.com' },
  { id: 3, name: 'Partner C', email: 'partnerc@example.com' },
  { id: 4, name: 'Partner D', email: 'partnerd@example.com' },
];

const AssignPartners = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requirementData, setRequirementData] = useState(null);
  const [partners] = useState(mockPartners);
  const [partnerAssignments, setPartnerAssignments] = useState([
    {
      id: Date.now(),
      partnerId: '',
      requiredCandidates: '',
      closingDate: '',
      notes: '',
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Get requirement data from sessionStorage
    const storedData = sessionStorage.getItem('pendingRequirement');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setRequirementData(data);
      } catch (error) {
        console.error('Error parsing requirement data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load requirement data',
          variant: 'destructive',
        });
        navigate('/source-candidates');
      }
    } else {
      navigate('/source-candidates');
    }

    // TODO: Replace with actual API call to fetch partners
    // fetchPartners();
  }, [id, navigate, toast]);

  // TODO: Replace with actual API call
  // const fetchPartners = async () => {
  //   try {
  //     setIsLoading(true);
  //     const response = await OrganizationService.listAllOrganizations(1, 100);
  //     setPartners(response.data.items || []);
  //   } catch (error) {
  //     console.error('Error fetching partners:', error);
  //     toast({
  //       title: 'Error',
  //       description: 'Failed to load partners',
  //       variant: 'destructive',
  //     });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const addPartnerAssignment = () => {
    setPartnerAssignments([
      ...partnerAssignments,
      {
        id: Date.now() + Math.random(),
        partnerId: '',
        requiredCandidates: '',
        closingDate: '',
        notes: '',
      },
    ]);
  };

  const removePartnerAssignment = (index) => {
    setPartnerAssignments(partnerAssignments.filter((_, i) => i !== index));
  };

  const updatePartnerAssignment = (index, field, value) => {
    const updated = [...partnerAssignments];
    updated[index] = { ...updated[index], [field]: value };
    setPartnerAssignments(updated);
  };

  const handleSubmit = async () => {
    // Validate all assignments
    for (let i = 0; i < partnerAssignments.length; i += 1) {
      const assignment = partnerAssignments[i];
      if (!assignment.partnerId) {
        toast({
          title: 'Warning',
          description: `Please select a partner for assignment ${i + 1}`,
          variant: 'warning',
        });
        return;
      }
      if (
        !assignment.requiredCandidates ||
        parseInt(assignment.requiredCandidates, 10) <= 0
      ) {
        toast({
          title: 'Warning',
          description: `Please enter a valid number of candidates for assignment ${i + 1}`,
          variant: 'warning',
        });
        return;
      }
      if (!assignment.closingDate) {
        toast({
          title: 'Warning',
          description: `Please select a closing date for assignment ${i + 1}`,
          variant: 'warning',
        });
        return;
      }
      // Validate closing date is not in the past
      const selectedDate = new Date(assignment.closingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        toast({
          title: 'Warning',
          description: `Closing date for assignment ${i + 1} must be today or in the future`,
          variant: 'warning',
        });
        return;
      }
    }

    // Check for duplicate partner selections
    const selectedPartnerIds = partnerAssignments.map(
      (assignment) => assignment.partnerId
    );
    const uniquePartnerIds = new Set(selectedPartnerIds);
    if (selectedPartnerIds.length !== uniquePartnerIds.size) {
      toast({
        title: 'Warning',
        description: 'Each partner can only be assigned once',
        variant: 'warning',
      });
      return;
    }

    try {
      setIsLoading(true);

      // TODO: Replace with actual API call
      // const assignmentsPayload = partnerAssignments.map((assignment) => ({
      //   partner_id: Number(assignment.partnerId),
      //   candidates_required: Number(assignment.requiredCandidates),
      //   closing_date: assignment.closingDate,
      //   notes: assignment.notes || '',
      // }));

      // Mock API call - replace with actual

      // Simulate API delay
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });

      toast({
        title: 'Success',
        description:
          'Requirement created and assigned to partners successfully',
        variant: 'success',
      });

      // Clear session storage
      sessionStorage.removeItem('pendingRequirement');

      // Navigate to requirements list
      navigate('/source-candidates');
    } catch (error) {
      console.error('Error submitting requirement:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to submit requirement',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!requirementData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading requirement data...</p>
      </div>
    );
  }

  return (
    <div className="text-left bg-[#F8FAFC] min-h-screen">
      <PageHeader
        title={`Assign Partners - ${requirementData.title}`}
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={() => navigate(`/source-candidates/preview/${id}`)}
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
            Back
          </Button>
        }
      />

      <PageContainer className="space-y-6">
        <div className="bg-card rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-foreground-strong mb-2">
              Assign Requirement to Partners
            </h2>
            <p className="text-sm text-gray-600">
              Assign this requirement to one or more partners. Each partner can
              have a different number of candidates required.
            </p>
          </div>

          <div className="space-y-4">
            {partnerAssignments.map((assignment, index) => (
              <div
                key={assignment.id}
                className="border border-gray-200 rounded-lg p-4 space-y-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground-strong">
                    Partner Assignment {index + 1}
                  </h3>
                  {partnerAssignments.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removePartnerAssignment(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Partner <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={assignment.partnerId}
                      onValueChange={(value) =>
                        updatePartnerAssignment(index, 'partnerId', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Partner" />
                      </SelectTrigger>
                      <SelectContent>
                        {partners.map((partner) => {
                          // Check if this partner is already assigned elsewhere
                          const isAssignedElsewhere = partnerAssignments.some(
                            (a, i) =>
                              a.partnerId === partner.id.toString() &&
                              i !== index
                          );
                          return (
                            <SelectItem
                              key={partner.id}
                              value={partner.id.toString()}
                              disabled={isAssignedElsewhere}
                            >
                              {partner.name}
                              {isAssignedElsewhere && ' (Already assigned)'}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Required Candidates{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={assignment.requiredCandidates}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (value === '' || parseInt(value, 10) > 0) {
                          updatePartnerAssignment(
                            index,
                            'requiredCandidates',
                            value
                          );
                        }
                      }}
                      placeholder="Enter number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Closing Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={assignment.closingDate}
                      onChange={(e) =>
                        updatePartnerAssignment(
                          index,
                          'closingDate',
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={assignment.notes}
                    onChange={(e) =>
                      updatePartnerAssignment(index, 'notes', e.target.value)
                    }
                    placeholder="Add any notes for this partner assignment"
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Button
              variant="outline"
              onClick={addPartnerAssignment}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Another Partner
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                Total Partners: {partnerAssignments.length}
              </p>
              <p className="text-sm text-gray-600">
                Total Candidates Required:{' '}
                {partnerAssignments.reduce(
                  (sum, assignment) =>
                    sum + (parseInt(assignment.requiredCandidates, 10) || 0),
                  0
                )}
              </p>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading ? 'Submitting...' : 'Submit Requirement'}
              {!isLoading && <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default AssignPartners;
